import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { gray } from "../../../../../theme/themePrimitives";

export interface PreviewAttribute {
  attributeKey: string;
  attributeCurrentValue?: string;
  attributeNewValue: string;
}

interface UserPreviewModalProps {
  open: boolean;
  loading: boolean;
  name: string;
  email: string;
  attributes: PreviewAttribute[];
  showDiff: boolean;
  onClose: () => void;
}

export const UserPreviewModal: React.FC<UserPreviewModalProps> = ({
  open,
  loading,
  name,
  email,
  attributes,
  showDiff,
  onClose,
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          maxHeight: "80vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          outline: "none",
        }}
      >
        <Box
          px={3}
          pt={3}
          pb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: `1px solid ${gray[200]}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonOutlineIcon sx={{ color: gray[500] }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {name || "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {email}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ border: "none" }}>
            <CloseIcon sx={{ color: gray[400] }} />
          </IconButton>
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: gray[400] }} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: showDiff ? "1fr 1fr 1fr" : "1fr 1fr",
                bgcolor: gray[50],
                px: 3,
                py: 1.5,
                borderBottom: `1px solid ${gray[100]}`,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={500}
                color="text.secondary"
              >
                Attribute
              </Typography>
              {showDiff && (
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.secondary"
                >
                  Current value
                </Typography>
              )}
              <Typography
                variant="body2"
                fontWeight={500}
                color="text.secondary"
              >
                {showDiff ? "New value" : "Value"}
              </Typography>
            </Box>

            {attributes.map((attr, i) => (
              <Box
                key={`${attr.attributeKey}-${i}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: showDiff ? "1fr 1fr 1fr" : "1fr 1fr",
                  px: 3,
                  py: 1.5,
                  borderTop: i > 0 ? `1px solid ${gray[100]}` : "none",
                }}
              >
                <Typography variant="body2">{attr.attributeKey}</Typography>
                {showDiff && (
                  <Typography variant="body2">
                    {attr.attributeCurrentValue}
                  </Typography>
                )}
                <Typography variant="body2">
                  {attr.attributeNewValue}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Modal>
  );
};
